/**
 * Services: Link Service.
 *
 * A service which handles the link/no link attribute for entity's occurrences.
 *
 * @since 3.11.0
 */

/**
 * Internal dependencies
 */
import EditorService from "../EditorService";
import {LinkServiceInterface} from "./LinkServiceInterface";

/**
 * Define the `LinkService` class.
 *
 * @since 3.11.0
 */
export default  class LinkService extends LinkServiceInterface {
  /**
   * Create an `LinkService` instance.
   *
   * @since 3.13.0
   */
  constructor() {
    // Set the `link by default` setting.
    super();
    this.linkByDefault = ( "1" === wlSettings.link_by_default );
  }

  /**
   * Set the link flag on the provided `occurrences`.
   *
   * @since 3.11.0
   * @param {Array} occurrences An array of occurrences ids (which match dom
   *     elements).
   * @param {boolean} value True to enable linking or false to disable it.
   */
  setLink(occurrences, value) {
    // If the request is to enable links, remove the `wl-no-link` class on
    // all the occurrences.
    if (value) {
      occurrences.forEach(x => this.setYesLink(x));
    } else {
      // If the request is to disable links, add the `wl-no-link` class to
      // all occurrences.
      occurrences.forEach(x => this.setNoLink(x));
    }
  }
  /**
   * Switch the link on.
   *
   * @since 3.13.0
   * @param {object} elem A DOM element.
   */
  setYesLink(elem) {
    const dom = EditorService.get().dom;
    dom.removeClass(elem, "wl-no-link");
    dom.addClass(elem, "wl-link");
  }
  /**
   * Switch the link off.
   *
   * @since 3.13.0
   * @param {object} elem A DOM element.
   */
  setNoLink(elem) {
    const dom = EditorService.get().dom;
    dom.removeClass(elem, "wl-link");
    dom.addClass(elem, "wl-no-link");
  }
  /**
   * Get the link flag given the provided `occurrences`. A link flag is
   * considered true when at least one occurrences enables linking.
   *
   * @since 3.11.0
   * @param {Array} occurrences An array of occurrences dom ids.
   * @return {boolean} True if at least one occurrences enables linking,
   *     otherwise false.
   */
  getLink(occurrences) {
    const ed = EditorService.get();
    if (ed) {
      // Handle classic editor
      return occurrences.reduce((acc, id) => {
        const dom = ed.dom;
        return acc || (this.linkByDefault ? !dom.hasClass(id, "wl-no-link") : dom.hasClass(id, "wl-link"));
      }, false);
    } else {
      // Handle block editor
      const divElem = document.createElement("div");
      divElem.innerHTML = wp.data
          .select("core/editor")
          .getBlocks()
          .map(block => block.originalContent)
          .join();
      return occurrences.reduce((acc, id) => {
        return (
            acc ||
            (this.linkByDefault
                ? !(
                    divElem.querySelector(`[id="${id}"]`) &&
                    divElem.querySelector(`[id="${id}"]`).classList.contains("wl-no-link")
                )
                : divElem.querySelector(`[id="${id}"]`) &&
                divElem.querySelector(`[id="${id}"]`).classList.contains("wl-link"))
        );
      }, false);
    }
  }
}
